import { useNode, Element, useEditor } from '@craftjs/core';
import {
  dateIntervalCalcDefaultPropsFactory,
  DIFFERENCE_UNITS,
  DifferenceType,
  IDateIntervalCalc,
  IDateIntervalCalcProps,
} from 'src/features/forms/base/controls/specials/DateIntervalCalc';
import { DateInput } from '../Inputs/DateInput';
import {
  calculatedInputPropsFactory,
  ICalculatedInputProps,
} from 'src/features/forms/base/controls/specials/CalculatedBlock';
import { useEffect, useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import { FormFillContext } from '../../uiEditor/provider/formFill.context';
import { dateInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/DateInput';
import { CalculatedBlock } from '../Containers/CalculatedBlock';
import { Container, ContainerSettings } from '../Containers/Container';
import { differenceInDays, differenceInMonths, differenceInWeeks, differenceInYears } from 'date-fns';
import { toDateOrNull } from 'src/helpers/date-helpers';
import { useTranslation } from 'react-i18next';
import { PanelContainer } from '../../toolbox/components/panelContainer';
import { ControlsGroup } from '../../toolbox/components/controlsGroup';
import { AppDropDownWithSuggestionInput } from 'uikit/inputs';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';

const IDS = {
  dateFromId: 'From',
  dateUntilId: 'Until',
  dateIntervalId: 'DateInterval',
} as const;

const useGetLinkedNodeDataKeys = () => {
  const { linkedNodes } = useNode((state) => ({
    linkedNodes: state.data.linkedNodes,
  }));

  const { getDataKeyByNodeId } = useEditor((state) => ({
    getDataKeyByNodeId: (nodeId: string) =>
      Object.keys(linkedNodes).length > 0 ? state.nodes[linkedNodes[nodeId]].data.props.dataKey : null,
  }));

  return useMemo(
    () => ({
      dateFrom: getDataKeyByNodeId(IDS.dateFromId),
      dateUntil: getDataKeyByNodeId(IDS.dateUntilId),
      dateInterval: getDataKeyByNodeId(IDS.dateIntervalId),
    }),
    [getDataKeyByNodeId],
  );
};

export const DateIntervalCalc: IDateIntervalCalc = (props) => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const dataKeys = useGetLinkedNodeDataKeys();

  const { setValue, removeValue } = useContextSelector(FormFillContext, (v) => v);
  const isOnlyView = useContextSelector(FormFillContext, (v) => v.mode === 'Overview');
  const fromValue = useContextSelector(FormFillContext, (v) => v.values?.[dataKeys.dateFrom]);
  const untilValue = useContextSelector(FormFillContext, (v) => v.values?.[dataKeys.dateUntil]);

  useEffect(() => {
    if (isOnlyView) return;

    if (!toDateOrNull(fromValue) || !toDateOrNull(untilValue)) {
      removeValue?.(dataKeys.dateInterval);
      return;
    }

    const getDifFunction = {
      days: differenceInDays,
      weeks: differenceInWeeks,
      months: differenceInMonths,
      years: differenceInYears,
    };
    const interval = getDifFunction[props.differenceUnits](toDateOrNull(untilValue)!, toDateOrNull(fromValue)!);

    // TODO: Don't calculate on edit form opening
    setValue?.(dataKeys.dateInterval, interval.toString(), { dontTouchField: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromValue, untilValue, isOnlyView]);

  return (
    <div ref={(ref: HTMLDivElement) => connect(drag(ref))}>
      <Container {...props}>
        <Element
          is={DateInput}
          id={IDS.dateFromId}
          label={'From'}
          {...dateInputDefaultPropsFactory()}
          hasSkipCheckBox={props.hasSkipCheckBox}
          size={'100%'}
        />
        <Element
          is={DateInput}
          id={IDS.dateUntilId}
          label={'Until'}
          {...dateInputDefaultPropsFactory()}
          hasSkipCheckBox={props.hasSkipCheckBox}
          size={'100%'}
        />
        <Element
          is={CalculatedBlock}
          id={IDS.dateIntervalId}
          {...calculatedInputPropsFactory()}
          label={'Interval'}
          size={'50%'}
          units={props.differenceUnits}
        />
      </Container>
    </div>
  );
};

const DateIntervalGeneralSettings = () => {
  const { t } = useTranslation('dev');
  const { actions } = useEditor();
  const { linkedNodes } = useNode((state) => ({
    linkedNodes: state.data.linkedNodes,
  }));

  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props as IDateIntervalCalcProps,
  }));

  return (
    <PanelContainer header={t('StudySettingsPage.FormBuilder.settings.dateIntervalSettings')} expanded={false}>
      <ControlsGroup flexible>
        <AppDropDownWithSuggestionInput
          isRequired={true}
          options={scoreGroupOptions}
          value={scoreGroupOptions.find((x) => x.key === props.differenceUnits)}
          onChange={(x) => {
            actions.setProp(linkedNodes[IDS.dateIntervalId], (setProps: ICalculatedInputProps) => {
              setProps.units = x?.key ?? dateIntervalCalcDefaultPropsFactory().differenceUnits;
            });

            setProp((setProps: IDateIntervalCalcProps) => {
              setProps.differenceUnits =
                (x?.key as DifferenceType) ?? dateIntervalCalcDefaultPropsFactory().differenceUnits;
            });
          }}
        />
      </ControlsGroup>
    </PanelContainer>
  );
};

const scoreGroupOptions = DIFFERENCE_UNITS.map((value) => {
  return { key: value, text: value } as DropdownOption<string>;
});

const DateIntervalCalcSettings = () => {
  return (
    <>
      <ContainerSettings />
      <DateIntervalGeneralSettings />
    </>
  );
};

DateIntervalCalc.craft = {
  defaultProps: dateIntervalCalcDefaultPropsFactory(),
  related: {
    settings: DateIntervalCalcSettings,
  },
};
